import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { FieldGrid, SelectInputField, TextInputField } from "@/base";
import {
  MARITAL_STATUSES,
  maritalStatusLabel,
  spouseSigns,
} from "@/lib/bondRoster";
import type { MaritalStatus } from "@/lib/brokerAccounts";
import {
  isOwner,
  personLabel,
  spouseCandidates,
  type PersonRow,
  type SpouseChoice,
} from "@/lib/accountRecordForm";

// BRK-S281 — an owner's share, marital status and spouse, on the account.
//
// PMS builds the GIA's signers from the ACCOUNT's people (PMS-S606): every
// owner (a share above 0) and the spouse of every owner who is married or in a
// domestic partnership. Those three facts were only editable inside the bond
// wizard's roster step, so the Required steps panel's "Complete the owners’
// details" led to a page that could not fix what it named. They live here now.
//
// The vocabulary is the wizard's own (`MARITAL_STATUSES` in bondRoster.ts —
// the ONE constant that holds it, labels and the spouse rule together), so the
// two surfaces can never word a status differently or disagree about whether
// it puts a spouse on the agreement.

const MARITAL_OPTIONS = MARITAL_STATUSES.map(({ value, label }) => ({
  value,
  label,
}));

/** The Spouse select's value for "someone not on the account yet". */
const NEW_SPOUSE = "__new__";

export function OwnerIndemnityFields({
  row,
  people,
  canEdit,
  onShare,
  onMaritalStatus,
  onSpouse,
}: {
  row: PersonRow;
  people: readonly PersonRow[];
  canEdit: boolean;
  onShare: (share: string) => void;
  onMaritalStatus: (status: MaritalStatus | "") => void;
  onSpouse: (choice: SpouseChoice) => void;
}) {
  const owner = isOwner(row);
  const asksSpouse = owner && spouseSigns(row.maritalStatus) === true;
  const spouse = row.spouse;
  const partnerIndex =
    spouse.kind === "person"
      ? people.findIndex((p) => p.key === spouse.key)
      : -1;
  const partner = partnerIndex >= 0 ? people[partnerIndex] : undefined;
  const partnerName = partner ? personLabel(partner, partnerIndex) : null;

  if (!canEdit) {
    const facts: string[] = [];
    if (owner) facts.push(`Ownership share: ${row.share.trim()}%`);
    if (owner) {
      facts.push(
        `Marital status: ${maritalStatusLabel(row.maritalStatus) ?? "Not recorded"}`,
      );
    }
    if (partnerName) facts.push(`Spouse: ${partnerName}`);
    return facts.length ? (
      <>
        {facts.map((f) => (
          <span key={f}>{f}</span>
        ))}
      </>
    ) : null;
  }

  const spouseValue =
    spouse.kind === "person"
      ? spouse.key
      : spouse.kind === "new"
        ? NEW_SPOUSE
        : "";
  const candidates = spouseCandidates(people, row.key).map((p) => ({
    value: p.key,
    label: personLabel(p, people.indexOf(p)),
  }));

  return (
    <Box
      component="fieldset"
      data-testid="owner-indemnity-fields"
      sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
    >
      <FieldGrid min={140}>
        <TextInputField
          label="Ownership share (%)"
          inputMode="decimal"
          value={row.share}
          onChange={onShare}
        />
        {owner && (
          <SelectInputField
            label="Marital status"
            placeholder="Not recorded"
            value={row.maritalStatus}
            options={MARITAL_OPTIONS}
            onChange={(v) => onMaritalStatus(v as MaritalStatus | "")}
          />
        )}
        {asksSpouse && (
          <SelectInputField
            label="Spouse"
            full
            placeholder="Not recorded"
            value={spouseValue}
            options={[
              ...candidates,
              { value: NEW_SPOUSE, label: "Someone not listed — add them" },
            ]}
            helperText="The spouse of an owner signs the indemnity agreement too."
            onChange={(v) =>
              onSpouse(
                v === ""
                  ? { kind: "none" }
                  : v === NEW_SPOUSE
                    ? { kind: "new", firstName: "", lastName: "", email: "" }
                    : { kind: "person", key: v },
              )
            }
          />
        )}
      </FieldGrid>
      {asksSpouse && spouse.kind === "new" && (
        <Box sx={{ mt: 2 }}>
          <FieldGrid min={140}>
            <TextInputField
              label="Spouse’s first name"
              value={spouse.firstName}
              onChange={(v) => onSpouse({ ...spouse, firstName: v })}
            />
            <TextInputField
              label="Spouse’s last name"
              value={spouse.lastName}
              onChange={(v) => onSpouse({ ...spouse, lastName: v })}
            />
            <TextInputField
              label="Spouse’s email"
              type="email"
              value={spouse.email}
              onChange={(v) => onSpouse({ ...spouse, email: v })}
            />
          </FieldGrid>
        </Box>
      )}
      {!asksSpouse && partnerName && (
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          {`Spouse of ${partnerName}`}
        </Typography>
      )}
    </Box>
  );
}
